<div class="page-container">
  <!-- Main Content -->
  <main class="page-main">
    <div class="page-content">
      <!--
        Header Section. No <h1> here: the tab bar carries the page's identity and the
        breadcrumb already names it, so a "Dashboard" heading immediately above a
        "Dashboard" tab was saying it twice. .page-title is shared with the other
        pages and stays as it is.
      -->
      <div class="page-title-container">
        <app-breadcrumb></app-breadcrumb>
      </div>

      <!--
        One card holding the tab bar. The @if guards must stay in the same order and
        use the same predicates as DashboardComponent.visibleTabKeys, or the selected
        index and the rendered tabs disagree.
      -->
      <mat-card class="dashboard-summary-card dashboard-tabs-card">
        <!--
          preserveContent keeps a panel in the DOM once it has been opened. Without it
          Material detaches the inactive panel, which destroys app-assignments-card and
          resets its own Active/Past toggle every time the user leaves the tab.
        -->
        <!--
          animationDuration 0ms drops Material's slide: the selected panel appears
          immediately instead of sliding in from the side.
        -->
        <mat-tab-group
          class="dashboard-tab-group"
          animationDuration="0ms"
          [mat-stretch-tabs]="false"
          [selectedIndex]="selectedTabIndex"
          [preserveContent]="true"
          (selectedIndexChange)="onTabChange($event)"
        >
          <!--
            Dashboard Tab. The three firm-wide areas share one tab, each section
            gated on its own read permission — so a user with only Matters read
            sees the tab with just the Clients & Matters section. The tab itself
            hangs off hasAnyDashboardPermission, which is what makes it absent for
            a basic user rather than an empty shell.
          -->
          @if (isPermissionsLoading || hasAnyDashboardPermission) {
            <mat-tab label="Dashboard">
              <div class="tab-panel">
                <div class="dashboard-grid">
                  <!-- Resources Card -->
                  @if (isPermissionsLoading || canViewResources) {
                    <mat-card
                      class="dashboard-summary-card resources-card"
                      [class.skeleton-card]="
                        isPermissionsLoading || isResourcesLoading
                      "
                    >
                      <!-- Card Header -->
                      <div class="card-header">
                        <h2 class="section-title">Resources</h2>
                        <div class="title-divider"></div>
                      </div>

                      <!-- Card Body -->
                      <div class="card-body-wrapper">
                        @if (isPermissionsLoading || isResourcesLoading) {
                          <div class="skeleton-body"></div>
                        } @else {
                          <!-- Recently Viewed Resources Table -->
                          <div class="recent-resources-section">
                            <h3 class="section-subtitle">Recently Viewed</h3>
                            @if (recentlyViewedResources.length > 0) {
                              <div class="recent-resources-table-container">
                                <table
                                  mat-table
                                  [dataSource]="recentlyViewedResources"
                                  class="recent-resources-table"
                                >
                                  <!-- First Name Column -->
                                  <ng-container matColumnDef="firstName">
                                    <th mat-header-cell *matHeaderCellDef>
                                      First Name
                                    </th>
                                    <td mat-cell *matCellDef="let resource">
                                      {{ resource.firstName }}
                                    </td>
                                  </ng-container>

                                  <!-- Last Name Column -->
                                  <ng-container matColumnDef="lastName">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Last Name
                                    </th>
                                    <td mat-cell *matCellDef="let resource">
                                      {{ resource.lastName }}
                                    </td>
                                  </ng-container>

                                  <!-- Practice Area Column -->
                                  <ng-container matColumnDef="practiceArea">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Practice Areas
                                    </th>
                                    <td mat-cell *matCellDef="let resource">
                                      @if (
                                        resource.practiceAreas &&
                                        resource.practiceAreas.length > 0
                                      ) {
                                        <app-membership-chips
                                          [items]="resource.practiceAreas"
                                          labelKey="description"
                                        />
                                      } @else {
                                        <span class="text-muted">—</span>
                                      }
                                    </td>
                                  </ng-container>

                                  <!-- Capacity Column -->
                                  <ng-container matColumnDef="capacity">
                                    <th
                                      mat-header-cell
                                      *matHeaderCellDef
                                      class="capacity-header"
                                    >
                                      <div class="capacity-header-container">
                                        <span>Capacity</span>
                                        <mat-icon
                                          class="capacity-info-icon"
                                          (mouseenter)="
                                            showCapacityLegendHover($event)
                                          "
                                          (mouseleave)="
                                            hideCapacityLegendHover()
                                          "
                                        >
                                          help_outline
                                        </mat-icon>
                                      </div>
                                    </th>
                                    <td mat-cell *matCellDef="let resource">
                                      <div class="capacity-dots">
                                        @for (
                                          weeklyCapacity of getWeeklyCapacities(
                                            resource
                                          );
                                          track $index
                                        ) {
                                          <svg
                                            class="status-dot-svg"
                                            [class.current-week]="$index === 1"
                                            viewBox="0 0 8 8"
                                            [matTooltip]="
                                              getCapacityTooltip(weeklyCapacity)
                                            "
                                            matTooltipPosition="above"
                                          >
                                            <circle
                                              cx="4"
                                              cy="4"
                                              [attr.r]="
                                                getCapacityStatusClass(
                                                  weeklyCapacity.statusCode
                                                ) === 'status-dot-unknown'
                                                  ? 3
                                                  : 4
                                              "
                                              class="status-dot {{
                                                getCapacityStatusClass(
                                                  weeklyCapacity.statusCode
                                                )
                                              }}"
                                            />
                                          </svg>
                                        }
                                      </div>
                                    </td>
                                  </ng-container>

                                  <tr
                                    mat-header-row
                                    *matHeaderRowDef="[
                                      'firstName',
                                      'lastName',
                                      'practiceArea',
                                      'capacity',
                                    ]"
                                  ></tr>
                                  <tr
                                    mat-row
                                    *matRowDef="
                                      let row;
                                      columns: [
                                        'firstName',
                                        'lastName',
                                        'practiceArea',
                                        'capacity',
                                      ]
                                    "
                                    class="clickable-row"
                                    (click)="navigateToResource(row.employeeId)"
                                  ></tr>
                                </table>
                              </div>
                            } @else {
                              <p class="no-items-message">
                                There are no recently viewed Resources
                              </p>
                            }
                          </div>

                          <!-- Summary Section -->
                          <div class="summary-section">
                            <h3 class="section-subtitle">
                              Summary of Current and Future Capacity
                            </h3>
                            <div class="card-stats-container">
                              <div class="card-stats">
                                <div class="stat-item danger">
                                  <span class="stat-value">{{
                                    dashboardData.resources.aboveCapacity
                                  }}</span>
                                  <span class="stat-label">Above Capacity</span>
                                </div>
                                <div class="stat-item success">
                                  <span class="stat-value">{{
                                    dashboardData.resources.atCapacity
                                  }}</span>
                                  <span class="stat-label">At Capacity</span>
                                </div>
                                <div class="stat-item warning">
                                  <span class="stat-value">{{
                                    dashboardData.resources.belowCapacity
                                  }}</span>
                                  <span class="stat-label">Below Capacity</span>
                                </div>
                                <div class="stat-item unknown">
                                  <span class="stat-value">{{
                                    dashboardData.resources.unknown
                                  }}</span>
                                  <span class="stat-label">Unknown</span>
                                </div>
                                <div class="stat-item not-available">
                                  <span class="stat-value">{{
                                    dashboardData.resources.notAvailable
                                  }}</span>
                                  <span class="stat-label">Not Available</span>
                                </div>
                              </div>
                              <div class="pie-chart-container">
                                <svg viewBox="0 0 100 100" class="pie-chart">
                                  <!-- Above Capacity (red) -->
                                  <circle
                                    r="25"
                                    cx="50"
                                    cy="50"
                                    fill="transparent"
                                    stroke="#9f3223"
                                    stroke-width="50"
                                    [attr.stroke-dasharray]="
                                      getStrokeDashArray(
                                        dashboardData.resources.aboveCapacity,
                                        getResourcesTotal()
                                      )
                                    "
                                    [attr.stroke-dashoffset]="0"
                                    transform="rotate(-90 50 50)"
                                  />
                                  <!-- At Capacity (orange) -->
                                  <circle
                                    r="25"
                                    cx="50"
                                    cy="50"
                                    fill="transparent"
                                    stroke="#f57c00"
                                    stroke-width="50"
                                    [attr.stroke-dasharray]="
                                      getStrokeDashArray(
                                        dashboardData.resources.atCapacity,
                                        getResourcesTotal()
                                      )
                                    "
                                    [attr.stroke-dashoffset]="
                                      getStrokeOffset(
                                        dashboardData.resources.aboveCapacity,
                                        getResourcesTotal()
                                      )
                                    "
                                    transform="rotate(-90 50 50)"
                                  />
                                  <!-- Below Capacity (green) -->
                                  <circle
                                    r="25"
                                    cx="50"
                                    cy="50"
                                    fill="transparent"
                                    stroke="#2e7d32"
                                    stroke-width="50"
                                    [attr.stroke-dasharray]="
                                      getStrokeDashArray(
                                        dashboardData.resources.belowCapacity,
                                        getResourcesTotal()
                                      )
                                    "
                                    [attr.stroke-dashoffset]="
                                      getStrokeOffset(
                                        dashboardData.resources.aboveCapacity +
                                          dashboardData.resources.atCapacity,
                                        getResourcesTotal()
                                      )
                                    "
                                    transform="rotate(-90 50 50)"
                                  />
                                  <!-- Unknown (gray) -->
                                  <circle
                                    r="25"
                                    cx="50"
                                    cy="50"
                                    fill="transparent"
                                    stroke="#9e9e9e"
                                    stroke-width="50"
                                    [attr.stroke-dasharray]="
                                      getStrokeDashArray(
                                        dashboardData.resources.unknown,
                                        getResourcesTotal()
                                      )
                                    "
                                    [attr.stroke-dashoffset]="
                                      getStrokeOffset(
                                        dashboardData.resources.aboveCapacity +
                                          dashboardData.resources.atCapacity +
                                          dashboardData.resources.belowCapacity,
                                        getResourcesTotal()
                                      )
                                    "
                                    transform="rotate(-90 50 50)"
                                  />
                                  <!-- Not Available (dark gray) -->
                                  <circle
                                    r="25"
                                    cx="50"
                                    cy="50"
                                    fill="transparent"
                                    stroke="#424242"
                                    stroke-width="50"
                                    [attr.stroke-dasharray]="
                                      getStrokeDashArray(
                                        dashboardData.resources.notAvailable,
                                        getResourcesTotal()
                                      )
                                    "
                                    [attr.stroke-dashoffset]="
                                      getStrokeOffset(
                                        dashboardData.resources.aboveCapacity +
                                          dashboardData.resources.atCapacity +
                                          dashboardData.resources
                                            .belowCapacity +
                                          dashboardData.resources.unknown,
                                        getResourcesTotal()
                                      )
                                    "
                                    transform="rotate(-90 50 50)"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        }
                      </div>

                      <!-- Card Footer -->
                      <div class="card-footer">
                        <a
                          class="view-all-link"
                          (click)="navigateTo('/resources')"
                        >
                          View All Resources
                          <mat-icon>arrow_forward</mat-icon>
                        </a>
                      </div>
                    </mat-card>
                  }

                  <!-- Requests Card -->
                  @if (isPermissionsLoading || canViewRequests) {
                    <mat-card
                      class="dashboard-summary-card requests-card"
                      [class.skeleton-card]="
                        isPermissionsLoading || isRequestsLoading
                      "
                    >
                      <!-- Card Header -->
                      <div class="card-header">
                        <h2 class="section-title">Requests</h2>
                        <div class="title-divider"></div>
                      </div>

                      <!-- Card Body -->
                      <div class="card-body-wrapper">
                        @if (isPermissionsLoading || isRequestsLoading) {
                          <div class="skeleton-body"></div>
                        } @else {
                          <!-- Recently Viewed Requests Table -->
                          <div class="recent-requests-section">
                            <h3 class="section-subtitle">Recently Viewed</h3>
                            @if (recentlyViewedRequests.length > 0) {
                              <div class="recent-requests-table-container">
                                <table
                                  mat-table
                                  [dataSource]="recentlyViewedRequests"
                                  class="recent-requests-table"
                                >
                                  <!-- Request ID Column -->
                                  <ng-container matColumnDef="id">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Request ID
                                    </th>
                                    <td mat-cell *matCellDef="let request">
                                      {{ request.id }}
                                    </td>
                                  </ng-container>

                                  <!-- Client-Matter Column -->
                                  <ng-container matColumnDef="clientMatter">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Client-Matter #
                                    </th>
                                    <td mat-cell *matCellDef="let request">
                                      {{ request.clientMatterNumber || "N/A" }}
                                    </td>
                                  </ng-container>

                                  <!-- Status Column -->
                                  <ng-container matColumnDef="status">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Status
                                    </th>
                                    <td mat-cell *matCellDef="let request">
                                      <span
                                        class="badge"
                                        [ngClass]="
                                          getStatusClass(request.status)
                                        "
                                      >
                                        {{
                                          request.status?.name ||
                                            request.status?.code ||
                                            "N/A"
                                        }}
                                      </span>
                                    </td>
                                  </ng-container>

                                  <!-- Assigned Column -->
                                  <ng-container matColumnDef="resources">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Assigned
                                    </th>
                                    <td mat-cell *matCellDef="let request">
                                      {{ getAssignedCount(request) }}
                                    </td>
                                  </ng-container>

                                  <tr
                                    mat-header-row
                                    *matHeaderRowDef="[
                                      'id',
                                      'clientMatter',
                                      'status',
                                      'resources',
                                    ]"
                                  ></tr>
                                  <tr
                                    mat-row
                                    *matRowDef="
                                      let row;
                                      columns: [
                                        'id',
                                        'clientMatter',
                                        'status',
                                        'resources',
                                      ]
                                    "
                                    class="clickable-row"
                                    (click)="navigateToRequest(row.id)"
                                  ></tr>
                                </table>
                              </div>
                            } @else {
                              <p class="no-items-message">
                                There are no recently viewed Requests
                              </p>
                            }
                          </div>

                          <!-- Summary Section -->
                          <div class="summary-section">
                            <h3 class="section-subtitle">
                              Summary of Total Requests
                            </h3>
                            <div class="card-stats-container">
                              <div class="card-stats">
                                <div class="stat-item warning">
                                  <span class="stat-value">{{
                                    dashboardData.requests.pending
                                  }}</span>
                                  <span class="stat-label">New</span>
                                </div>
                                <div class="stat-item success">
                                  <span class="stat-value">{{
                                    dashboardData.requests.assigned
                                  }}</span>
                                  <span class="stat-label">Pending</span>
                                </div>
                              </div>
                              <div class="pie-chart-container">
                                <svg viewBox="0 0 100 100" class="pie-chart">
                                  <!-- Open (green) -->
                                  <circle
                                    r="25"
                                    cx="50"
                                    cy="50"
                                    fill="transparent"
                                    stroke="#729a4cff"
                                    stroke-width="50"
                                    [attr.stroke-dasharray]="
                                      getStrokeDashArray(
                                        dashboardData.requests.pending,
                                        getRequestsTotal()
                                      )
                                    "
                                    [attr.stroke-dashoffset]="0"
                                    transform="rotate(-90 50 50)"
                                  />
                                  <!-- Pending (orange) -->
                                  <circle
                                    r="25"
                                    cx="50"
                                    cy="50"
                                    fill="transparent"
                                    stroke="#f57c00"
                                    stroke-width="50"
                                    [attr.stroke-dasharray]="
                                      getStrokeDashArray(
                                        dashboardData.requests.assigned,
                                        getRequestsTotal()
                                      )
                                    "
                                    [attr.stroke-dashoffset]="
                                      getStrokeOffset(
                                        dashboardData.requests.pending,
                                        getRequestsTotal()
                                      )
                                    "
                                    transform="rotate(-90 50 50)"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        }
                      </div>

                      <!-- Card Footer -->
                      <div class="card-footer">
                        <a
                          class="view-all-link"
                          (click)="navigateTo('/requests')"
                        >
                          View All Requests
                          <mat-icon>arrow_forward</mat-icon>
                        </a>
                      </div>
                    </mat-card>
                  }

                  <!-- Clients & Matters Card -->
                  @if (isPermissionsLoading || canViewClientMatters) {
                    <mat-card
                      class="dashboard-summary-card clients-card"
                      [class.skeleton-card]="
                        isPermissionsLoading || isMattersLoading
                      "
                    >
                      <!-- Card Header -->
                      <div class="card-header">
                        <h2 class="section-title">Clients & Matters</h2>
                        <div class="title-divider"></div>
                      </div>

                      <!-- Card Body -->
                      <div class="card-body-wrapper">
                        @if (isPermissionsLoading || isMattersLoading) {
                          <div class="skeleton-body"></div>
                        } @else {
                          <!-- Recently Viewed Matters Table -->
                          <div class="recent-matters-section">
                            <h3 class="section-subtitle">Recently Viewed</h3>
                            @if (recentlyViewedMatters.length > 0) {
                              <div class="recent-matters-table-container">
                                <table
                                  mat-table
                                  [dataSource]="recentlyViewedMatters"
                                  class="recent-matters-table"
                                >
                                  <!-- Client-Matter Column -->
                                  <ng-container matColumnDef="clientMatter">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Client Matter
                                    </th>
                                    <td mat-cell *matCellDef="let matter">
                                      <app-matter-cell
                                        [matterNumber]="
                                          matter.matter.clientMatterNumber
                                        "
                                        [matterName]="matter.matter.matterName"
                                        [clientName]="matter.client?.clientName"
                                        [link]="true"
                                        (clicked)="navigateToMatter($event)"
                                      />
                                    </td>
                                  </ng-container>

                                  <!-- Region Column -->
                                  <ng-container matColumnDef="region">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Region
                                    </th>
                                    <td mat-cell *matCellDef="let matter">
                                      {{ getRegion(matter) }}
                                    </td>
                                  </ng-container>

                                  <!-- Last Viewed Column -->
                                  <ng-container matColumnDef="lastViewed">
                                    <th mat-header-cell *matHeaderCellDef>
                                      Last Viewed
                                    </th>
                                    <td mat-cell *matCellDef="let matter">
                                      {{ formatLastViewed(matter.accessedAt) }}
                                    </td>
                                  </ng-container>

                                  <!-- Actions Column -->
                                  <ng-container matColumnDef="actions">
                                    <th
                                      mat-header-cell
                                      *matHeaderCellDef
                                      class="actions-cell"
                                    >
                                      Actions
                                    </th>
                                    <td
                                      mat-cell
                                      *matCellDef="let matter"
                                      class="actions-cell"
                                    >
                                      <div class="actions-cell-inner">
                                        <button
                                          mat-icon-button
                                          class="action-btn"
                                          matTooltip="View matter details"
                                          (click)="
                                            navigateToMatter(
                                              matter.matter.clientMatterNumber
                                            )
                                          "
                                        >
                                          <mat-icon>visibility</mat-icon>
                                        </button>
                                        @if (isComparisonPopUpsEnabled) {
                                          <button
                                            mat-icon-button
                                            class="action-btn"
                                            matTooltip="Open in floating window"
                                            (click)="
                                              openMatterInFloatingDialog(
                                                matter.matter,
                                                $event
                                              )
                                            "
                                          >
                                            <mat-icon>select_window_2</mat-icon>
                                          </button>
                                        }
                                      </div>
                                    </td>
                                  </ng-container>

                                  <tr
                                    mat-header-row
                                    *matHeaderRowDef="[
                                      'clientMatter',
                                      'region',
                                      'lastViewed',
                                      'actions',
                                    ]"
                                  ></tr>
                                  <tr
                                    mat-row
                                    *matRowDef="
                                      let row;
                                      columns: [
                                        'clientMatter',
                                        'region',
                                        'lastViewed',
                                        'actions',
                                      ]
                                    "
                                  ></tr>
                                </table>
                              </div>
                            } @else {
                              <p class="no-items-message">
                                There are no recently viewed Clients & Matters
                              </p>
                            }
                          </div>
                        }
                      </div>

                      <!-- Card Footer -->
                      <div class="card-footer">
                        <a
                          class="view-all-link"
                          (click)="navigateTo('/clients-matters')"
                        >
                          View All Clients & Matters
                          <mat-icon>arrow_forward</mat-icon>
                        </a>
                      </div>
                    </mat-card>
                  }

                  <!--
                    Recent Activity Card, gated on the recent-activity flag (kept from
                    its earlier life as a dedicated tab). Rows are visibility-filtered
                    server-side, so the flag is the only client-side gate; the feed is
                    told whether this tab is selected so live-refresh signals arriving
                    while the user looks elsewhere only mark it stale.
                  -->
                  @if (flagsResolved && isRecentActivityEnabled) {
                    <mat-card class="dashboard-summary-card activity-card">
                      <div class="card-header">
                        <h2 class="section-title">Recent Activity</h2>
                        <div class="title-divider"></div>
                      </div>
                      <app-activity-feed
                        [active]="activeTab === 'dashboard'"
                      ></app-activity-feed>
                    </mat-card>
                  }
                </div>
              </div>
            </mat-tab>
          }

          <!--
            My Time Management Tab (self-service, no permission gate). Capacity and
            Time Off sit side by side at half width each. The tab itself is ungated
            because Capacity is; when time-off-tracking is off the Time Off half is
            absent and the grid collapses Capacity to full width — see
            .time-management-split.
          -->
          <mat-tab label="My Time Management">
            <div class="tab-panel">
              <div class="time-management-split">
                <section class="time-management-pane">
                  <!--
                    app-capacity-management renders its own mat-card, so it needs no
                    wrapper here; the unavailable state does, matching user-profile.
                  -->
                  @if (myEmployeeId) {
                    <app-capacity-management
                      [employeeId]="myEmployeeId"
                      [isReadOnly]="false"
                      [canEditCapacitySettings]="canEditCapacitySettings"
                      (editModeRequested)="onCapacityEditModeRequested()"
                    ></app-capacity-management>
                  } @else {
                    <mat-card class="capacity-management-card">
                      <div class="no-capacity-message">
                        <mat-icon class="no-capacity-icon">info</mat-icon>
                        <h3>Capacity Management Unavailable</h3>
                        <p class="no-capacity-text">
                          This user cannot set capacity. Please contact your
                          administrator.
                        </p>
                      </div>
                    </mat-card>
                  }
                </section>

                @if (isTimeOffTrackingEnabled) {
                  <section class="time-management-pane">
                    <!--
                      app-time-off-management renders a bare container, so the card
                      is supplied here exactly as user-profile supplies it.
                    -->
                    <mat-card class="time-off-card">
                      @if (myEmployeeId) {
                        <app-time-off-management
                          [entries]="timeOffEntries"
                          [lookupTypes]="timeOffTypeLookups"
                          [isReadOnly]="!isTimeOffApprovalsEnabled"
                          [apiMode]="'self-service'"
                          [employeeId]="myEmployeeId"
                          [employeeName]="currentUser"
                          (entriesSaved)="onMyTimeOffSaved()"
                          (requestStatusChanged)="onMyTimeOffSaved()"
                          (editModeRequested)="onTimeOffEditModeRequested()"
                        ></app-time-off-management>
                      } @else {
                        <div class="no-capacity-message">
                          <mat-icon class="no-capacity-icon">info</mat-icon>
                          <h3>Time Off Unavailable</h3>
                          <p class="no-capacity-text">
                            This user cannot record time off. Please contact
                            your administrator.
                          </p>
                        </div>
                      }
                    </mat-card>
                  </section>
                }
              </div>
            </div>
          </mat-tab>

          <!--
            My Assignments Tab (self-service, no permission gate). The card is main's
            my-assignments-card, unchanged — only its location moved.
          -->
          <mat-tab label="My Assignments">
            <div class="tab-panel">
              <mat-card class="dashboard-summary-card my-assignments-card">
                <div class="card-header">
                  <h2 class="section-title">My Assignments</h2>
                  <div class="title-divider"></div>
                </div>

                <div class="card-body-wrapper">
                  <app-assignments-card
                    mode="resource"
                    [assignments]="myAssignmentRows"
                    [isLoading]="isMyAssignmentsLoading"
                    [emptyMessageOverride]="myAssignmentsEmptyMessage"
                    [showManagerNotes]="canViewAssignmentNotes"
                    [showHeader]="false"
                    [showRequestId]="false"
                    [showPaginator]="false"
                    [showFilter]="false"
                    [canViewMatter]="canViewClientMatters"
                    [canViewRequest]="canViewRequests"
                    (matterClicked)="navigateToMatter($event)"
                  ></app-assignments-card>
                </div>

                @if (showMyResourceLink) {
                  <div class="card-footer">
                    <a class="view-all-link" (click)="navigateToMyResource()">
                      View My Resource Details
                      <mat-icon>arrow_forward</mat-icon>
                    </a>
                  </div>
                }
              </mat-card>
            </div>
          </mat-tab>

          <!--
            My Notifications Tab (self-service; gated on the in-app-notifications
            flag). app-notification-list renders a bare container, so the card is
            supplied here as user-profile supplies it.
          -->
          @if (!flagsResolved || isNotificationsEnabled) {
            <mat-tab label="My Notifications">
              <div class="tab-panel">
                <mat-card class="notification-card">
                  <app-notification-list />
                </mat-card>
              </div>
            </mat-tab>
          }

        </mat-tab-group>
      </mat-card>
    </div>
  </main>

  <!-- Capacity Legend Hover Card -->
  @if (showCapacityLegend) {
    <div
      class="capacity-legend-hover-card"
      [style.left.px]="capacityLegendPosition.x"
      [style.top.px]="capacityLegendPosition.y"
    >
      <mat-card class="hover-card-content">
        <mat-card-content>
          <app-capacity-legend></app-capacity-legend>
        </mat-card-content>
      </mat-card>
    </div>
  }
</div>
